<template lang="pug">
  .game#game(ref="game", :class="{ 'game_big' : gameOver || final }", @contextmenu="stopContextMenu($event)")
    .game__border(v-for="item in borders", :class="'game__border_' + item")
    .game__info
      LivesCounter(:count="playerData.lives", :maxValue="defaultLives")
      PointsCounter(:count="playerData.points")
    .game__power
      PowerScale(:count="playerData.power", :maxValue="defaultPower")
    .game__buttons
      Buttons(:isPlaying="gameData.musicPlaying", @music="toggleMusic", @pause="togglePause")
    Attempt(v-if="gameOver", @reset="reset", :points="playerData.points")
    Final(v-if="final", @reset="reset", :points="playerData.points")
    transition(name="game", mode="out-in")
      Restart(v-if="!gameOver && !final && gameData.stopGame", @restart="restart")
      Pause(v-if="gameData.isPaused", @continue="togglePause")
      Loading(v-if="gameData.loadPercent !== 100", :percent="gameData.loadPercent")
</template>

<script>
/* eslint-disable no-unused-vars */
import 'p2';
import 'pixi';
import Phaser from 'phaser-ce';
/* eslint-enable no-unused-vars */

import { mapGetters } from 'vuex';
import gameConsts from '@/js/gameConsts';

import PowerScale from '@/components/Interface/PowerScale';
import PointsCounter from '@/components/Interface/PointsCounter';
import LivesCounter from '@/components/Interface/LivesCounter';
import Restart from '@/components/Interface/Restart';
import Pause from '@/components/Interface/Pause';
import Loading from '@/components/Interface/Loading';
import Buttons from '@/components/Interface/Buttons';
import Final from '@/components/Interface/Final';
import Attempt from '@/components/Interface/Attempt';

import Road from '@/js/Road';
import Player from '@/js/Player';
import Shield from '@/js/Shield';
import Power from '@/js/Power';
import DoublePoints from '@/js/DoublePoints';
import Hole from '@/js/Hole';
import Spikes from '@/js/Spikes';
import Enemy from '@/js/Enemy';
import MovingEnemy from '@/js/MovingEnemy';
import Level from '@/js/Level';
import Control from '@/js/Control';

let globalTimer;
let timer;
let spaceKey;
let music;
let game = null;
let controls = {};
let gameObjects = {
  hole: {}, // dummy for spikes creation
  movingEnemy: {}, // dummy for enemy creation
  enemy: {}, // dummy for spikes creation
};
const isDebug = false;
const levelSignal = new Phaser.Signal();
const buttons = ['left', 'right'];
const levels = ['level1', 'level2', 'level3'];
const enemies = ['enemy', 'movingEnemy'];
const obstacles = ['hole', 'spikes'];
const roadItems = ['power', 'shield', 'doublePoints', ...enemies, ...obstacles];
const allItems = [...roadItems, ...levels];
const PLAYER_VALUES = {
  points: 0,
  lives: 3,
  power: 36,
  speedY: 4,
  velocity: 260,
  pointsMultipler: 1,
  collision: false,
  isShield: false,
};
const GAME_VALUES = {
  musicPlaying: false,
  stopGame: false,
  isRestart: false,
  isLoaded: false,
  isPaused: false,
  loadPercent: 0,
  time: 0,
};

export default {
  name: 'Game',
  components: {
    PowerScale,
    PointsCounter,
    LivesCounter,
    Restart,
    Pause,
    Loading,
    Buttons,
    Final,
    Attempt,
  },
  computed: {
    ...mapGetters(['tries', 'isLicense']),
    checkCollision() {
      return !this.playerData.isShield && this.playerData.collision;
    },
    checkStopGame() {
      return this.checkCollision || gameObjects.road.stop || this.playerData.power === 0;
    },
    gameOver() {
      return this.playerData.lives === 0 && this.tries > 0; // попытки
    },
    final() {
      return this.playerData.lives === 0 && (this.tries === 0 || !this.isLicense);
      // return true;
    },
    defaultLives() {
      return PLAYER_VALUES.lives;
    },
    defaultPower() {
      return PLAYER_VALUES.power;
    },
  },
  beforeDestroy() {
    if (game !== null) {
      this.destroyGame();
    }
  },
  mounted() {
    if (game == null) {
      this.createGame();
    }
  },
  methods: {
    resizeGame() {
      this.scaleGame();
      if (!this.gameData.stopGame && !this.isPaused) {
        this.gameData.isPaused = true;
        game.gamePaused();
      }
    },
    signalHandler(name) {
      gameObjects[gameConsts.LEVELS[name].nextLevel].isActive = true;
      gameObjects[name].maxIterations += 2;
      gameObjects.road.increaseSpeed();
      gameObjects.player.increaseSpeed();
    },
    createGame() {
      game = new Phaser.Game(gameConsts.GAME_WIDTH, gameConsts.GAME_HEIGHT, Phaser.AUTO, 'game', {
        preload: () => {
          this.preload();
        },
        create: () => {
          this.create();
        },
        update: () => {
          this.update();
        },
        render: () => {
          if (isDebug) {
            game.debug.body(gameObjects.power.item);
            game.debug.body(gameObjects.shield.item);
            game.debug.body(gameObjects.hole.item);
            game.debug.body(gameObjects.spikes.item);
            game.debug.body(gameObjects.player.item);
            game.debug.body(gameObjects.enemy.item);
            game.debug.body(gameObjects.doublePoints.item);
            game.debug.body(gameObjects.movingEnemy.item);
          }
        },
      });
      this.setDefaultValues();
      window.addEventListener('resize', this.resizeGame);
    },
    destroyGame() {
      game.destroy();
      game = null;
      gameObjects = {};
      controls = {};

      this.isSent = false;

      levelSignal.remove(this.signalHandler);

      window.removeEventListener('resize', this.resizeGame);
    },
    checkCollisions(objName) {
      if (!this.gameData.isRestart && game.physics.arcade.collide(gameObjects.player.item, gameObjects[objName].item)) {
        this.playerData.collision = true;
      }
    },
    scaleGame() {
      ['player', 'road', ...allItems].forEach((item) => {
        gameObjects[item].scaleItem();
      });
      if (Phaser.Device.touch) {
        this.createControls();
        buttons.forEach((item) => {
          controls[item].update();
        });
      } else if (Object.keys(controls).length !== 0) {
        buttons.forEach((item) => {
          controls[item].delete();
        });
      }
    },
    preload() {
      game.load.audio('music', ['/static/sounds/music.mp3']);
      game.load.onLoadStart.add(() => {
        this.gameData.loadPercent = 0;
      });
      game.load.onFileComplete.add((progress) => {
        this.gameData.loadPercent = progress;
      });
      game.load.onLoadComplete.add(() => {
        this.gameData.loadPercent = 100;
        this.gameData.isLoaded = true;
      });
      game.load.atlasJSONHash('nfs', '/static/img/nfs.png', '/static/img/nfs.json');
      game.load.image('left', '/static/img/control_left.png');
      game.load.image('right', '/static/img/control_right.png');
    },
    create() {
      this.createObjects();
      this.startTimer();
      this.startGlobalTimer();

      spaceKey = game.input.keyboard.addKey(Phaser.Keyboard.SPACEBAR);
      spaceKey.onDown.add(this.togglePause, this);

      // TODO: fix safari music
      music = game.add.audio('music');

      if (process.env.NODE_ENV !== 'development') {
        music.play();
        music.loopFull();
        this.gameData.musicPlaying = true;
      }

      gameObjects.level1.isActive = true;

      ['player', ...allItems].forEach((item) => {
        gameObjects[item].create();
      });

      this.scaleGame();

      levelSignal.add(this.signalHandler, this);
    },
    update() {
      if (!this.gameData.isLoaded) {
        this.gameData.isLoaded = true;
      } else if (!this.gameData.stopGame && !this.gameData.isPaused) {
        ['road', 'player', ...allItems].forEach((item) => {
          gameObjects[item].update();
        });

        [...enemies, ...obstacles].forEach((itemName) => {
          if (gameObjects[itemName].isActive) {
            this.checkCollisions(itemName);
          }
        });

        if (gameObjects.player.controls.left && gameObjects.player.controls.right) {
          game.input.reset();
        }

        if (!this.gameData.isRestart) {
          this.stop();
        } else {
          this.gameData.isRestart = false;
        }
      } else if (this.gameData.stopGame && !this.gameData.isPaused) {
        enemies.forEach((item) => {
          gameObjects[item].moveStatic();
        });
      }
      if (this.gameOver || this.final) {
        game.gamePaused();
        music.pause();
      }
    },
    createControls() {
      if (Object.keys(controls).length === 0) {
        buttons.forEach((item) => {
          controls[item] = new Control({ game, name: item, player: gameObjects.player });
        });
      }
    },
    createObjects() {
      const defaultData = {
        game,
        values: this.playerData,
      };

      gameObjects.road = new Road(defaultData);
      defaultData.road = gameObjects.road;

      levels.forEach((item) => {
        gameObjects[item] = new Level({ ...defaultData, signal: levelSignal, name: item });
      });

      gameObjects.player = new Player(defaultData);
      defaultData.player = gameObjects.player;

      gameObjects.doublePoints = new DoublePoints(defaultData);
      gameObjects.power = new Power(defaultData);
      gameObjects.shield = new Shield(defaultData);

      gameObjects.spikes = new Spikes(defaultData);
      defaultData.spikes = gameObjects.spikes;
      gameObjects.hole = new Hole({ ...defaultData, collideItem: gameObjects.spikes });
      gameObjects.spikes.collideItem = gameObjects.hole;

      gameObjects.enemy = new Enemy(defaultData);
      gameObjects.movingEnemy = new MovingEnemy({ ...defaultData, collideItem: gameObjects.enemy });
      gameObjects.enemy.collideItem = gameObjects.movingEnemy;
      gameObjects.spikes.enemies = [gameObjects.enemy, gameObjects.movingEnemy];
    },
    startTimer() {
      if (timer) {
        game.time.events.remove(timer);
      }
      timer = game.time.events.loop(Phaser.Timer.SECOND, this.updateValues, this);
    },
    startGlobalTimer() {
      if (globalTimer) {
        game.time.events.remove(globalTimer);
      }
      globalTimer = game.time.events.loop(1, () => {
        if (!this.gameOver && !this.gameData.stopGame && !this.final && !this.gameData.isPaused) {
          this.gameData.time += 1;
        }
      }, this);
    },
    setDefaultValues() {
      // задаём дефолтные значения
      Object.keys(PLAYER_VALUES).forEach((i) => {
        this.$set(this.playerData, i, PLAYER_VALUES[i]);
      });
      Object.keys(GAME_VALUES).forEach((i) => {
        this.$set(this.gameData, i, GAME_VALUES[i]);
      });
    },
    updateValues() {
      if (!this.gameData.stopGame && this.gameData.isLoaded && !this.gameData.isPaused) {
        gameObjects.player.updatePoints();
      }
    },
    restart() {
      this.playerData.collision = PLAYER_VALUES.collision;
      this.playerData.isShield = PLAYER_VALUES.isShield;
      this.playerData.power = PLAYER_VALUES.power;
      ['player', ...roadItems].forEach((item) => {
        gameObjects[item].restart();
      });
      this.startTimer();
      this.gameData.isRestart = true;
      this.gameData.stopGame = false;
    },
    stopContextMenu(e) {
      e.preventDefault();
    },
    reset() {
      this.destroyGame();
      this.createGame();
    },
    stop() {
      if (this.checkStopGame) {
        gameObjects.player.stop(this.checkCollision);
        this.gameData.stopGame = true;
        this.playerData.lives -= 1;
        game.input.reset();
      }
      this.playerData.collision = false;
    },
    togglePause() {
      if (!this.gameData.stopGame && this.gameData.isLoaded) {
        this.gameData.isPaused = !this.gameData.isPaused;
        if (this.gameData.isPaused) {
          game.gamePaused();
        } else {
          game.gameResumed();
        }
      }
    },
    toggleMusic() {
      if (music.isPlaying) {
        music.pause();
      } else {
        music.resume();
      }
      this.gameData.musicPlaying = music.isPlaying;
    },
  },
  data() {
    return {
      isSent: false,
      playerData: {},
      gameData: {},
      borders: ['top', 'left', 'right', 'bottom'],
    };
  },
};
</script>

<style lang="scss">
  .game {
    position: relative;
    @include vertical-align;
    justify-content: flex-end;
    flex-direction: column;
    width: $frame-width;
    height: $frame-height;
    @include vue-trans;
    @media (max-width: $max-width-xs) {
      width: auto;
      height: 100%;
      overflow: hidden;
      max-height: $frame-height;
      min-height: $game-height-xs;
    }
    &__border {
      @include frame-borders;
    }
    &__info,
    &__buttons {
      position: absolute;
      right: 56px;
    }
    &__info {
      @include vertical-align;
      top: 46px;
      left: 64px;
      justify-content: space-between;
      @media (max-width: $max-width-xs) {
        background: rgba($clr-black, $opacity);
        top: 0;
        left: 0;
        right: 0;
        padding: 20px 24px 5px;
      }
    }
    &__buttons {
      bottom: 70px;
      @media (-moz-touch-enabled: 1),
        (pointer: coarse) {
        top: 148px;
        bottom: auto;
      }
      @media (max-width: $max-width-xs) {
        top: 78px;
        right: 30px;
      }
    }
    &__power {
      position: absolute;
      left: 53px;
      bottom: 56px;
      @media (-moz-touch-enabled: 1),
        (pointer: coarse) {
        bottom: auto;
        top: 92px;
      }
      @media (max-width: $max-width-xs) {
        top: 28px;
        left: 30px;
      }
    }
    &_big {
      display: block;
      max-height: none;
      overflow: auto;
      overflow-x: hidden;
      min-height: auto;
      canvas {
        top: -100%;
        position: absolute;
      }
    }
    &_big & {
      &__border {
        display: none;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Game.vue